import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MailCheck, Loader2, AlertCircle } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "working", message: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState({ status: "error", message: "This verification link is missing its token." }); return; }
    axios.post(`${API}/auth/verify-email`, { token })
      .then(async ({ data }) => {
        setUser(data);
        setState({ status: "done", message: "" });
        const intentRaw = localStorage.getItem("dhdh_beta_intent");
        if (intentRaw) {
          localStorage.removeItem("dhdh_beta_intent");
          try {
            const intent = JSON.parse(intentRaw);
            const res = await axios.post(`${API}/payments/subscribe`, { plan: `${intent.tier}_${intent.interval || "annual"}`, origin_url: window.location.origin });
            const url = res.data.checkout_url || res.data.url;
            if (url) { window.location.href = url; return; }
          } catch { /* fall through to plan picker */ }
        }
        setTimeout(() => navigate(data.requires_choice ? "/membership/choose" : "/dashboard"), 1500);
      })
      .catch((e) => setState({ status: "error", message: e.response?.data?.detail || "We couldn't verify this link." }));
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-4">
      <div className="max-w-md w-full bg-white border border-[#E5DFEC] rounded-2xl p-8 text-center" data-testid="verify-email-page">
        {state.status === "working" && <><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#6B4E8A]" /><p className="mt-4 text-[#64748B]">Verifying your email…</p></>}
        {state.status === "done" && (
          <div data-testid="verify-email-success">
            <MailCheck className="w-10 h-10 mx-auto text-[#059669]" />
            <h1 className="mt-4 text-2xl text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>Email verified</h1>
            <p className="mt-2 text-sm text-[#64748B]">Taking you in…</p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="verify-email-error">
            <AlertCircle className="w-10 h-10 mx-auto text-[#DC2626]" />
            <h1 className="mt-4 text-2xl text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>Link not valid</h1>
            <p className="mt-2 text-sm text-[#64748B]">{state.message}</p>
            <Button asChild className="mt-6 rounded-full bg-[#6B4E8A] text-white"><Link to="/login" data-testid="verify-email-login-link">Go to sign in</Link></Button>
          </div>
        )}
      </div>
    </div>
  );
}
